import { TunaDivider as Divider } from '../primitives/divider.js'
import { Typography } from '../primitives/index.js'
import moduleStyles from './share-list.module.css'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { Owned } from '@tunasong/models'
import { hasPublicAccess } from '@tunasong/models'
import type { ACL } from '@tunasong/schemas'

import type { FC } from 'react'
import { UserAvatar } from '../profile/index.js'
import ACLList from './acl-list.js'
import { permissionLabel } from './permission-label.js'
import { memberLabel, useMemberEmails } from './member-emails.hook.js'
import { List, ListItem, ListItemIcon, ListItemText } from '../primitives/index.js'

export interface ShareListProps {
  className?: string
  entity?: Owned
  /** Pending users */
  pending?: string[]
  /** Id for the Collaborators heading, so focus can move to it (request-access spec §8.4) */
  headingId?: string
  onDelete(acl: ACL): void
}

export const ShareList: FC<ShareListProps> = props => {
  const { className, pending = [], entity, headingId, onDelete } = props
  const acls: ACL[] = entity?.acls ?? []
  const { profile: ownerProfile } = graphHooks.useProfile(entity?.userId)
  const emails = useMemberEmails(entity)

  if (!Array.isArray(acls)) {
    return null
  }

  const ownerLabel = memberLabel(ownerProfile, emails) ?? 'Loading...'
  const ownerEmail = entity ? emails[entity.userId] : undefined
  const ownerSecondary = ownerEmail && ownerEmail !== ownerLabel ? `${ownerEmail} · Owner` : 'Owner'

  return (
    <div className={className}>
      <List>
        <ListItem>
          <ListItemIcon>
            <UserAvatar aria-label="user" userId={entity?.userId} />
          </ListItemIcon>
          <ListItemText primary={ownerLabel} secondary={ownerSecondary} />
        </ListItem>
        {hasPublicAccess(acls) && (
          <ListItem>
            <ListItemIcon>
              <UserAvatar aria-label="public" userId="PUBLIC" />
            </ListItemIcon>
            <ListItemText primary={'Public'} secondary={permissionLabel('READ')} />
          </ListItem>
        )}
        <Divider />
      </List>
      {acls.length > 0 && (
        <Typography
          variant="subtitle1"
          className={moduleStyles.collaboratorsTitle}
          id={headingId}
          tabIndex={headingId ? -1 : undefined}
        >
          Collaborators
        </Typography>
      )}
      <ACLList entity={entity} onDelete={onDelete} pendingPrincipals={pending} />
    </div>
  )
}

export default ShareList
